import { useApolloClient } from '@apollo/client';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from '@headlessui/react';
import {
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/react/20/solid';
import {
  FaceFrownIcon,
  IdentificationIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { GET_ADMIN_SEARCH } from '@retainerclub/admin-api';
import { useApp } from '@retainerclub/hooks';
import { GET_PARTNER_SEARCH } from '@retainerclub/partner-api';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

const LOCS = 'Partner locations';
const ORGS = 'Partner organizations';

const RESULT_ROUTES = {
  Accounts: {
    admin: (id) => `/customers/accounts/${id}`,
    partner: (id) => `/customers/accounts/${id}`,
  },
  [LOCS]: {
    admin: (id) => `/partners/locations/${id}`,
    partner: (id) => `/locations/${id}`,
  },
  [ORGS]: {
    admin: (id) => `/partners/organizations/${id}`,
  },
  'Partner users': {
    admin: (id) => `/partners/users/${id}`,
    partner: (id) => `/users/${id}`,
  },
  'Admin users': {
    admin: (id) => `/admins/users/${id}`,
  },
  Patients: {
    admin: (id) => `/customers/patients/${id}`,
    partner: (id) => `/customers/patients/${id}`,
  },
  Products: {
    admin: (id) => `/shop/products/${id}`,
  },
  Users: {
    admin: (id) => `/customers/users/${id}`,
    partner: (id) => `/customers/users/${id}`,
  },
};

function Search(props) {
  const { open, setOpen } = props;
  const navigate = useNavigate();
  const client = useApolloClient();

  const [searchState, setSearchState] = useState({
    results: [],
    value: '',
  });

  const { results, value } = searchState;

  const { isAdmin, isPartner } = useApp();

  useEffect(() => {
    async function performQuery() {
      let query = GET_ADMIN_SEARCH;
      if (isPartner) {
        query = GET_PARTNER_SEARCH;
      }

      const { data } = await client.query({
        query,
        variables: { q: value },
        fetchPolicy: 'no-cache',
      });

      const formattedData = [...data.search]
        .map((search) => {
          const { attributes } = search;
          return attributes;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      setSearchState((prevState) => ({
        ...prevState,
        results: formattedData,
      }));
    }

    const timer = setTimeout(() => {
      if (value.length === 0) {
        return setSearchState({
          results: [],
          value: '',
        });
      }

      return performQuery();
    }, 500);

    return () => clearTimeout(timer);
  }, [client, isPartner, value]);

  const filteredItems = value === '' ? [] : results;

  const groups = filteredItems.reduce((g, item) => {
    if (!g[item.klass]) {
      g[item.klass] = [];
    }
    g[item.klass].push(item);
    return g;
  }, {});

  // Define the priority order for displaying groups
  const priorityOrder = [
    'Accounts',
    'Users',
    'Patients',
    LOCS,
    ORGS,
    'Partner users',
    'Products',
    'Admin users',
  ];

  // Sort the groups according to priority order
  const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
    const aIndex = priorityOrder.indexOf(a);
    const bIndex = priorityOrder.indexOf(b);
    // If both items are in priority order, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    // If only one item is in priority order, it comes first
    if (aIndex !== -1) {
      return -1;
    }
    if (bIndex !== -1) {
      return 1;
    }
    // If neither item is in priority order, maintain alphabetical order
    return a.localeCompare(b);
  });

  function handleSearchChange(val) {
    setSearchState((prevState) => ({
      ...prevState,
      value: val,
    }));
  }

  function resetSearch() {
    setSearchState((prevState) => ({
      ...prevState,
      value: '',
    }));
  }

  function handleResultSelect(result) {
    setOpen(false);

    if (!result) {
      return;
    }

    const routes = RESULT_ROUTES[result.klass];
    const buildPath = isAdmin ? routes?.admin : routes?.partner;

    if (!buildPath) {
      console.warn(
        `Search: no route for "${result.klass}" (isAdmin: ${isAdmin})`,
      );
      return;
    }

    navigate(buildPath(result.id));
  }

  function iconColor(klass) {
    if (klass === 'Accounts') {
      return 'bg-violet-600';
    }

    if (klass === 'Patients') {
      return 'bg-purple-600';
    }

    if (klass === 'Users') {
      return 'bg-fuchsia-600';
    }

    if (klass === 'Partner users') {
      return 'bg-sky-600';
    }

    if (klass === 'Admin users') {
      return 'bg-green-600';
    }

    if (klass === LOCS) {
      return 'bg-cyan-600';
    }

    if (klass === ORGS) {
      return 'bg-teal-600';
    }

    return '';
  }

  function getDescription(item) {
    if (item.klass === LOCS || item.klass === ORGS) {
      return (
        <span>
          {item.city}, {item.region}
        </span>
      );
    }

    if (item.klass === 'Patients' || item.klass === 'Users') {
      return (
        <span>
          <span className="font-semibold">acct:</span>{' '}
          <span className="font-normal text-white/80">
            {item.accountNumber}
          </span>
          &nbsp;&nbsp;•&nbsp;&nbsp;
          <span className="font-semibold">location:</span>{' '}
          <span className="font-normal text-white/80">
            {item?.partnerLocation?.name}
          </span>
        </span>
      );
    }

    if (item.klass === 'Partner users') {
      return (
        <span>
          <span className="font-semibold">org:</span>{' '}
          <span className="font-normal text-white/80">
            {item?.partnerOrganization?.name}
          </span>
          {item?.partnerLocation?.name && (
            <>
              &nbsp;&nbsp;•&nbsp;&nbsp;
              <span className="font-semibold">location:</span>{' '}
              <span className="font-normal text-white/80">
                {item?.partnerLocation?.name}
              </span>
            </>
          )}
        </span>
      );
    }

    if (item.klass === 'Accounts') {
      return (
        <span>
          <span className="font-semibold">location:</span>{' '}
          <span className="font-normal text-white/80">
            {item?.partnerLocation?.name}
          </span>
        </span>
      );
    }

    return '';
  }

  function getImageOrIcon(item) {
    if (item.imageUrl) {
      return (
        <img
          src={item.imageUrl}
          alt={item.name}
          className="h-10 w-10 rounded-lg"
        />
      );
    }

    if (item.klass === LOCS) {
      return (
        <BuildingOfficeIcon className="h-6 w-6 text-white" aria-hidden="true" />
      );
    }

    if (item.klass === ORGS) {
      return (
        <BuildingOffice2Icon
          className="h-6 w-6 text-white"
          aria-hidden="true"
        />
      );
    }

    if (item.klass === 'Products') {
      return <UserIcon className="h-6 w-6 text-white" aria-hidden="true" />;
    }

    return (
      <img
        src="https://cdn.retainerclub.com/images/defaults/default_partner_image.jpg"
        alt={item.name}
        className="h-10 w-10"
      />
    );
  }

  return (
    <Dialog
      open={open}
      className="relative z-70"
      onClose={() => {
        setOpen(false);
        resetSearch();
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/70 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-xl transform divide-y divide-white/10 overflow-hidden rounded-xl bg-gray-900 shadow-2xl outline-1 outline-white/10 -outline-offset-1 transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in"
        >
          <Combobox onChange={(item) => handleResultSelect(item)}>
            <div className="grid grid-cols-1">
              <ComboboxInput
                autoFocus
                className="col-start-1 row-start-1 h-12 w-full bg-gray-900 pr-4 pl-11 text-base text-white outline-hidden placeholder:text-gray-500 focus:border-transparent focus:ring-transparent sm:text-sm"
                placeholder="Search..."
                onChange={(event) => handleSearchChange(event.target.value)}
              />
              <MagnifyingGlassIcon
                className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-500"
                aria-hidden="true"
              />
            </div>

            {filteredItems.length > 0 && (
              <ComboboxOptions
                static
                as="ul"
                className="max-h-96 scroll-py-2 divide-y divide-white/10 overflow-y-auto py-2 text-gray-800 text-sm dark:text-gray-300"
              >
                {sortedGroups.map(([category, res]) => (
                  <li key={category} className="p-2">
                    <h2 className="mt-4 mb-2 px-3 font-semibold text-gray-500 text-xs capitalize dark:text-gray-40">
                      {category}
                    </h2>
                    <ul className="text-gray-300 text-sm">
                      {res.map((item) => (
                        <ComboboxOption
                          as="li"
                          key={item.id}
                          value={item}
                          className="group flex cursor-default select-none items-center gap-3 rounded-md px-3 py-2 data-focus:bg-white/5 data-focus:text-white data-focus:outline-hidden"
                        >
                          <div
                            className={`flex size-10 flex-none items-center justify-center rounded-lg ${iconColor(
                              item.klass,
                            )}`}
                          >
                            {(item.klass === 'Users' ||
                              item.klass === 'Admin users' ||
                              item.klass === 'Partner users') && (
                              <UserIcon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                              />
                            )}

                            {item.klass === 'Accounts' && (
                              <UserGroupIcon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                              />
                            )}

                            {item.klass === 'Patients' && (
                              <IdentificationIcon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                              />
                            )}

                            {(item.klass === LOCS ||
                              item.klass === ORGS ||
                              item.klass === 'Products') &&
                              getImageOrIcon(item)}
                          </div>
                          <div className="ml-4 flex-auto">
                            <p className="font-medium text-sm text-white/90 group-data-focus:text-white">
                              {item.name}
                            </p>
                            <p className="text-sm text-white/50 group-data-focus:text-white/70">
                              {getDescription(item)}
                            </p>
                          </div>
                        </ComboboxOption>
                      ))}
                    </ul>
                  </li>
                ))}
              </ComboboxOptions>
            )}

            {value !== '' && filteredItems.length === 0 && (
              <div className="border-white/20 border-t px-6 py-14 text-center text-sm sm:px-14">
                <FaceFrownIcon
                  className="mx-auto h-6 w-6 text-rcgray-500"
                  aria-hidden="true"
                />
                <p className="mt-4 font-semibold text-rcgray-200">
                  No results found
                </p>
                <p className="mt-2 text-rcgray-500">
                  We couldn&apos;t find anything with that term. Please try
                  again.
                </p>
              </div>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

Search.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default Search;
